import type { LoaderFunctionArgs } from "@remix-run/node";
import { Link, data, useLoaderData } from "@remix-run/react";
import { nanoid } from "nanoid";
import { stateCookie } from "~/cookies/oauthstate.server";
import { getSession } from "~/cookies/session.server";
import { TWITCH_CLIENT_ID, TWITCH_LOGIN_CALLBACK_URL } from "~/settings.server";

export const meta = () => [{ title: "Votekick | Because we can't expect mods to do all the work" }];

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const state = nanoid(32);
  const session = await getSession(request.headers.get("Cookie"));

  const errorMessage = session.get("errorMessage") ?? "";

  return data(
    {
      state,
      errorMessage,
      clientId: TWITCH_CLIENT_ID,
      callbackUrl: TWITCH_LOGIN_CALLBACK_URL,
    },
    {
      headers: {
        "Set-Cookie": await stateCookie.serialize(state),
      },
    },
  );
};

export default function LoginLanding() {
  const { clientId, callbackUrl, state, errorMessage } = useLoaderData<typeof loader>();
  const scopes = [
    "user:write:chat",
    "user:read:chat",
    "user:bot",
    "channel:bot",
    "moderation:read",
    "moderator:manage:banned_users",
    "channel:manage:polls",
  ];

  return (
    <main className="h-screen w-screen flex flex-col bg-[#e3dff2]">
      {errorMessage ? (
        <div className="px-16 w-screen absolute inset-x-0 h-fit py-2 font-main bg-[#ff4911] font-medium text-lg border-black border-b-2">
          <p className="w-max mx-auto">Something went wrong on our end... please try again later!</p>
        </div>
      ) : null}
      <div className="bg-grid">
        <header className="my-20 lg:my-28 w-5/6 lg:w-fit px-4 lg:px-12 py-4 mx-auto bg-[#fff1e8] border-black border-[3px] shadow-highlight ">
          <div className="p-4 lg:p-8 flex flex-col items-center gap-y-8">
            <h1 className="text-3xl lg:text-4xl font-title drop-shadow text-center">
              !votekick: Because we can't expect mods to do all the work
            </h1>
            <Link
              to={`https://id.twitch.tv/oauth2/authorize?response_type=code&scope=${encodeURIComponent(scopes.join(" "))}&state=${encodeURIComponent(state)}&client_id=${clientId}&redirect_uri=${callbackUrl}`}
              className="border-black border-4 px-4 py-2 bg-[#6441a5] transition duration-100 active:shadow-none active:border-[#6441a5] active:translate-x-1 active:translate-y-1 shadow-button text-white flex items-center gap-x-2 font-semibold font-twitch text-sm hover:bg-[#7c5cb8]"
            >
              <svg
                aria-hidden={true}
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
              >
                <path
                  d="M2.149 0l-1.612 4.119v16.836h5.731v3.045h3.224l3.045-3.045h4.657l6.269-6.269v-14.686h-21.314zm19.164 13.612l-3.582 3.582h-5.731l-3.045 3.045v-3.045h-4.836v-15.045h17.194v11.463zm-3.582-7.343v6.262h-2.149v-6.262h2.149zm-5.731 0v6.262h-2.149v-6.262h2.149z"
                  fillRule="evenodd"
                  clipRule="evenodd"
                />
              </svg>
              Login with Twitch
            </Link>
          </div>
        </header>
      </div>

      <div className="pt-12 lg:pt-16 pb-4 lg:pb-0 text-black w-screen font-main border-t-4 bg-[#ff964f] border-black h-full font-bold">
        <div className="container flex flex-col lg:flex-row gap-y-8 justify-around mx-auto px-8 lg:px-16">
          <section className="flex flex-col gap-y-2 shadow-button border-4 border-black bg-white p-6 lg:p-12">
            <h2 className="text-3xl lg:text-4xl">Features:</h2>
            <ul className="list-disc pl-6 text-baseline lg:text-lg">
              <li>Configurable messages</li>
              <li>Automated cleanup on stream end</li>
              <li>Moderator protection (optional, discouraged)</li>
              <li>Manual unbanning</li>
              <li>Funi</li>
            </ul>
          </section>
          <section className="flex flex-col gap-y-2 shadow-button border-4 border-black bg-white p-6 lg:p-12">
            <h2 className="text-3xl lg:text-4xl">Coming soon:</h2>
            <ul className="list-disc pl-6 text-baseline lg:text-lg">
              <li>More reliability</li>
              <li>More config options (unbannable users, etc.)</li>
              <li>VIP Protection</li>
              <li>Oomfie explode</li>
              <li>Votekick 2</li>
            </ul>
          </section>
        </div>
      </div>
      <footer className="text-black py-2 justify-around flex items-center bottom-0 inset-x-0 bg-[#ff964f] font-footer">
        <p className="drop-shadow text-xs lg:text-baseline">
          Made with love and lack of sleep by{" "}
          <Link to="https://twitch.tv/skyisfine" className="font-bold hover:underline">
            ~sky
          </Link>
        </p>
        <a href="https://ko-fi.com/E1E7177VOG" target="_blank" rel="noreferrer" className="group">
          <img
            height="36"
            className="h-9 rounded-full border-black border-2 shadow-button transition duration-100 group-hover:translate-x-1 group-hover:translate-y-1 group-hover:shadow-none"
            src="https://storage.ko-fi.com/cdn/kofi6.png?v=6"
            alt="Buy Me a Coffee at ko-fi.com"
          />
        </a>
      </footer>
    </main>
  );
}
